import React, { useMemo, useState } from "react"
import moment from "moment"
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Tooltip from "react-bootstrap/Tooltip"
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap"

import awicon from "../../../assets/images/services-icon/coin.png"
import reel from "../../../assets/images/services-icon/reel.png"

import { usePlanLessonLocal } from "./PlanLessonContext"
import {
  getUniqueTopicsNames,
  getDiagnosticsStats,
  getDiagnosticHighlightClass,
  getDiagnosticsStatusMeta,
  getDiagnosticsAttemptedText,
  getDiagnosticsScoreText,
  getDiagnosticsLevelPrefix,
  getQuestionBadgeClass,
  getCheckboxProps,
} from "./PlanLessonHelpers"
import type { DiagnosticsActivityLike, SessionLike } from "./PlanLessonTypes"

interface LocalDiagnosticsActivityCardProps {
  isHomework: boolean
  isPlanningSession: boolean
  activity: DiagnosticsActivityLike
  sessionData: SessionLike
  index: number
  enrolment?: any
}

function getRolledOverText(rollDate: unknown): string | null {
  if (!rollDate) {
    return null
  }

  return `Rolled over from ${moment(rollDate, moment.ISO_8601).format(
    "DD/MMM/YYYY"
  )}`
}

function renderQuestionBadgeContent(
  score: number | null,
  index: number
): React.ReactNode {
  if (score === 1 || score === 2) {
    return <i className="fas fa-check"></i>
  }

  if (score === 0) {
    return <i className="mdi mdi-window-close"></i>
  }

  return index + 1
}

function getCompactActionButtonStyle(
  tone: "secondary" | "menu"
): React.CSSProperties {
  const baseStyle: React.CSSProperties = {
    color: "#1b1b1b",
    border: "none",
    minHeight: "24px",
    padding: "4px 6px",
    lineHeight: 1.05,
    borderRadius: "4px",
    fontSize: "10px",
  }

  if (tone === "menu") {
    return {
      ...baseStyle,
      backgroundColor: "#d8deec",
    }
  }

  return {
    ...baseStyle,
    backgroundColor: "#d8deec",
  }
}

const CompactDiagnosticsActionButton = ({
  label,
  onClick,
  title,
}: {
  label: React.ReactNode
  onClick: () => unknown
  title?: string
}) => {
  return (
    <button
      type="button"
      className="btn btn-sm w-100 fw-700"
      title={title}
      style={{
        ...getCompactActionButtonStyle("secondary"),
        whiteSpace: "nowrap",
        paddingLeft: "2px",
        paddingRight: "2px",
        fontSize: "8px",
        overflow: "visible",
        lineHeight: 1.1,
      }}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => {
        event.stopPropagation()
        void onClick()
      }}
    >
      {label}
    </button>
  )
}

export const LocalDiagnosticsActivityCard = ({
  isHomework,
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
}: LocalDiagnosticsActivityCardProps) => {
  const controller = usePlanLessonLocal()
  const [contextMenuOpen, setContextMenuOpen] = useState(false)

  const { totalCoins, totalQuestion, countAnswer, questionStates } =
    getDiagnosticsStats(activity)

  const checkbox = getCheckboxProps({
    controller,
    isPlanningSession,
    sessionData,
    activity,
    enrolment,
  })

  const isTutorOwner =
    controller.sessionStudentData?.session?.fk_tut_key === activity.fk_tut_key

  const statusMeta = getDiagnosticsStatusMeta(activity.status)
  const rolledOverText = getRolledOverText(activity?.roll_date)
  const attemptedText = getDiagnosticsAttemptedText(countAnswer, totalQuestion)
  const scoreText = getDiagnosticsScoreText(sessionData, activity)
  const levelPrefix = getDiagnosticsLevelPrefix(activity.level_name)

  const topicNames =
    activity.topics && activity.topics.length > 0
      ? getUniqueTopicsNames(activity.topics)
      : null

  const titleText = `${levelPrefix || ""}${topicNames || ""}`.trim()

  const toggleContextMenu = () => setContextMenuOpen(prev => !prev)

  const handlePreview = () => controller.onPreviewActivity(activity as any)

  const handleViewActivities = () => {
    controller.tog_Diagnostics()
    void controller.fetchNextDiagnosticsActivityData(activity as any)
  }


  const handleAddComment = () => controller.handle_commend(activity as any)

  const handleDelete = () =>
    controller.onDelectActivityPopup(activity.pk_lesson_activity_key)

  const handleCopyToHomework = () =>
    controller.onActivityToHomeLesson(activity.pk_lesson_activity_key, 1)

  const handleCopyToLessonPlan = () =>
    controller.onActivityToHomeLesson(activity.pk_lesson_activity_key, 0)

  const handleMoveToHomework = () =>
    controller.onActivityToHomework(activity.pk_lesson_activity_key, 1)

  const handleMoveToLessonPlan = () =>
    controller.onActivityToHomework(activity.pk_lesson_activity_key, 0)

  const handleRollOverToNextSession = () =>
    controller.onMoveActivityPopup(activity as any)

  const handleRolloverToNextLesson = () =>
    controller.onRolloverActivityToNextSession(activity as any, 0)

  const handleRolloverToHomework = () =>
    controller.onRolloverActivityToNextSession(activity as any, 1)

  const handleCopySessionToHomework = () =>
    controller.onMoveHomeworkActivityPopup(activity as any)

  const deleteEnabled =
    controller.sessionStudentData?.session?.fk_tut_key === activity.fk_tut_key

  const sessionMenuItems = useMemo(() => {
    const items: Array<{
      key: string
      label: string
      onClick: () => unknown
      icon?: string
    }> = []

    if (sessionData?.session) {
      items.push({
        key: "rollOverToNextSession",
        label: "Roll over to next session",
        icon: "mdi mdi-skip-forward",
        onClick: handleRollOverToNextSession,
      })
    }

    items.push({
      key: "preview",
      label: "Preview",
      icon: "mdi mdi-eye-outline",
      onClick: handlePreview,
    })

    if (!isHomework && Number(activity.marked_for_homework) === 0) {
      items.push({
        key: "copyToHomework",
        label: "Copy to homework",
        icon: "mdi mdi-home-edit-outline",
        onClick: handleCopySessionToHomework,
      })
    }

    if (deleteEnabled) {
      items.push({
        key: "delete",
        label: "Delete",
        icon: "far fa-trash-alt",
        onClick: handleDelete,
      })
    }

    return items
  }, [activity, isHomework, deleteEnabled, sessionData?.session])

  if (!isPlanningSession) {
    return (
      <div
        className={`${getDiagnosticHighlightClass(
          sessionData,
          activity
        )} set1 bg-supermild`}
        data-key={activity.pk_lesson_activity_key}
        style={{
          minHeight: "unset",
          padding: "4px 6px",
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
              <div
                className="d-flex align-items-center fw-400 text-dark"
                style={{ fontSize: "8px", lineHeight: 1.1 }}
              >
                <span>
                  <img className="me-05" width={12} src={reel} alt="" />
                </span>
                Diagnostics
              </div>

              {rolledOverText ? (
                <span
                  className="text-danger"
                  style={{ fontSize: "8px", lineHeight: 1 }}
                >
                  {rolledOverText}
                </span>
              ) : null}

              {statusMeta.label ? (
                <span
                  className={`${statusMeta.className}`}
                  style={{ fontSize: "8px", lineHeight: 1 }}
                >
                  {statusMeta.label}
                </span>
              ) : null}

              <span
                className={`${statusMeta.className}`}
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {attemptedText}
              </span>

              {scoreText ? (
                <span
                  className="text-danger"
                  style={{ fontSize: "8px", lineHeight: 1 }}
                >
                  {scoreText}
                </span>
              ) : null}

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip className="custom-tooltip">
                    Comment: {activity?.tutor_comments || "No comments available"}
                  </Tooltip>
                }
              >
                <span className="no-bdr" style={{ cursor: "pointer" }}>
                  <i className="fas fa-info-circle"></i>
                </span>
              </OverlayTrigger>

              {checkbox?.checkable ? (
                <input
                  type="checkbox"
                  checked={!!checkbox.checked}
                  onMouseDown={event => event.stopPropagation()}
                  onChange={event => {
                    event.stopPropagation()
                    void checkbox.onCheckedChanged?.()
                  }}
                />
              ) : null}
            </div>

            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <h6
                  className="text-dark fw-700"
                  style={{
                    fontSize: "10px",
                    lineHeight: 1.2,
                    marginBottom: "3px",
                    overflow: "hidden",
                    wordBreak: "break-word",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                  title={titleText}
                >
                  {index + 1}) {titleText || "Diagnostics"}
                </h6>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-1">
                  <div className="question-count mt-0">
                    <ul
                      className="list-unstyled mb-0 d-flex align-items-center flex-wrap"
                      style={{ gap: "2px" }}
                    >
                      {questionStates.map((score, badgeIndex) => {
                        const normalizedScore =
                          typeof score === "number" && Number.isFinite(score)
                            ? score
                            : null

                        return (
                          <li
                            key={badgeIndex}
                            className={getQuestionBadgeClass(normalizedScore)}
                            style={{
                              minWidth: "17px",
                              height: "15px",
                              fontSize: "8px",
                              lineHeight: "15px",
                              padding: "0 3px",
                              textAlign: "center",
                              borderRadius: "8px",
                            }}
                          >
                            {renderQuestionBadgeContent(
                              normalizedScore,
                              badgeIndex
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>

                  <div
                    className="d-flex align-items-center fw-400 text-dark"
                    style={{ fontSize: "9px", lineHeight: 1.05 }}
                  >
                    <img className="me-05" width={12} src={awicon} alt="" />
                    {activity.coins_earned ? activity.coins_earned : 0}/{totalCoins}
                  </div>
                </div>
              </div>

              <div
                style={{
                  width: "168px",
                  flex: "0 0 168px",
                }}
              >
                <div className="d-grid" style={{ gap: "3px" }}>
                  {sessionData?.session ? (
                    <div
                      className="d-grid"
                      style={{ gridTemplateColumns: "1fr 1fr", gap: "3px" }}
                    >
                      <CompactDiagnosticsActionButton
                        label={
                          <>
                            <i
                              className="mdi mdi-redo"
                              style={{ fontSize: "10px", marginRight: "2px" }}
                              aria-hidden="true"
                            />
                            To lesson
                          </>
                        }
                        onClick={handleRolloverToNextLesson}
                        title="Rollover to next Lesson"
                      />
                      <CompactDiagnosticsActionButton
                        label={
                          <>
                            <i
                              className="mdi mdi-redo"
                              style={{ fontSize: "10px", marginRight: "2px" }}
                              aria-hidden="true"
                            />
                            To h/w
                          </>
                        }
                        onClick={handleRolloverToHomework}
                        title="Rollover to Homework"
                      />
                    </div>
                  ) : (
                    <div style={{ minHeight: "24px" }} />
                  )}

                  {sessionMenuItems.length > 0 ? (
                    <Dropdown
                      isOpen={contextMenuOpen}
                      toggle={toggleContextMenu}
                      onMouseDown={event => event.stopPropagation()}
                      onClick={event => event.stopPropagation()}
                    >
                      <DropdownToggle
                        caret={false}
                        className="btn btn-sm w-100 fw-700"
                        style={getCompactActionButtonStyle("menu")}
                      >
                        <i className="mdi mdi-dots-horizontal"></i>
                      </DropdownToggle>

                      <DropdownMenu className="custom-position">
                        {sessionMenuItems.map(item => (
                          <DropdownItem
                            key={item.key}
                            onClick={event => {
                              event.stopPropagation()
                              void item.onClick()
                            }}
                          >
                            {item.icon ? (
                              <i className={`${item.icon} font-size-15 me-1`}></i>
                            ) : null}
                            {item.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  ) : (
                    <div style={{ minHeight: "24px" }} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${getDiagnosticHighlightClass(
        sessionData,
        activity
      )} set1 bg-supermild`}
      data-key={activity.pk_lesson_activity_key}
    >
      <div className="d-flex justify-content-between">
        <div>
          <span className="text-danger font-size-12 me-2">
            {rolledOverText}
          </span>

          <span className={`${statusMeta.className} font-size-12 me-2`}>
            {statusMeta.label}
          </span>

          <span className={`${statusMeta.className} font-size-12 me-2`}>
            {attemptedText}
          </span>

          <span className="text-danger font-size-12 me-2">{scoreText}</span>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip className="custom-tooltip">
                Comment: {activity?.tutor_comments || "No comments available"}
              </Tooltip>
            }
          >
            <span className="no-bdr" style={{ cursor: "pointer" }}>
              <i className="fas fa-info-circle"></i>
            </span>
          </OverlayTrigger>
        </div>
      </div>

      <h6 className="text-dark border-bottom pb-2 mb-2 mt-2 font-size-13 lh-1">
        {index + 1 + ") "}
        {levelPrefix}
        {topicNames}
      </h6>

      <div className="d-flex justify-content-between mb-2">
        <div className="d-flex align-items-center fw-400 font-size-12 text-dark">
          <span>
            <img className="me-05" width={24} src={reel} alt="" />
          </span>
          Diagnostics
        </div>

        <Dropdown isOpen={contextMenuOpen} toggle={toggleContextMenu}>
          <DropdownToggle className="p-0 bg-white">
            <i className="mdi mdi-dots-horizontal"></i>
          </DropdownToggle>

          <DropdownMenu className="custom-position">
            {!isPlanningSession && sessionData?.session ? (
              <>
                <DropdownItem onClick={handleRollOverToNextSession}>
                  <span></span> Roll over to next session
                </DropdownItem>

                {!isHomework && Number(activity.marked_for_homework) === 0 ? (
                  <DropdownItem onClick={handleCopySessionToHomework}>
                    <span></span> Copy to homework
                  </DropdownItem>
                ) : null}
              </>
            ) : null}

            {isPlanningSession && controller.sessionStudentData?.session ? (
              <>
                {!isHomework ? (
                  <DropdownItem onClick={handleCopyToHomework}>
                    <span></span> Copy To Homework
                  </DropdownItem>
                ) : (
                  <DropdownItem onClick={handleCopyToLessonPlan}>
                    <span></span> Copy To Lesson Plan
                  </DropdownItem>
                )}
              </>
            ) : null}

            <DropdownItem onClick={handlePreview}>Preview</DropdownItem>

            {isPlanningSession ? (
              <>
                <DropdownItem onClick={handleViewActivities}>
                  View Activities
                </DropdownItem>


                <DropdownItem onClick={handleAddComment}>
                  Add Comment
                </DropdownItem>

                {!isHomework ? (
                  <DropdownItem onClick={handleMoveToHomework}>
                    Move To Homework
                  </DropdownItem>
                ) : (
                  <DropdownItem onClick={handleMoveToLessonPlan}>
                    Move To Lesson Plan
                  </DropdownItem>
                )}
              </>
            ) : null}

            {isTutorOwner ? (
              <DropdownItem onClick={handleDelete}>Delete</DropdownItem>
            ) : null}
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center fw-400 font-size-12 text-dark"></div>
        <div className="d-flex align-items-center fw-400 font-size-12 text-dark">
          <img className="me-05" width={24} src={awicon} alt="" />
          {activity.coins_earned ? activity.coins_earned : 0}/{totalCoins}
        </div>
      </div>

      <div className="question-count mt-2">
        <ul className="list-unstyled mb-0 d-flex align-items-center gap-1 mt-1">
          {questionStates.map((score, badgeIndex) => {
            const normalizedScore =
              typeof score === "number" && Number.isFinite(score) ? score : null

            return (
              <li
                key={badgeIndex}
                className={getQuestionBadgeClass(normalizedScore)}
              >
                {renderQuestionBadgeContent(normalizedScore, badgeIndex)}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
